import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenericPopUp } from "../../components/popup/genericPopUp";
import { DownloadSingleReportConfig, grnRowsData, popupType } from "../../types/types";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { InputLabel, MenuItem, FormControl, Select } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import dayjs, { Dayjs } from "dayjs";
import { Alert, Button, Snackbar, Stack, TextField } from "@mui/material";
import { ViewAllGrnConfig } from "./ViewAllGrnConfig";
import { getGrnList, getUTCDate } from "../../services/manageOrderService";
import "./ViewAllGrn.css";
import "../viewOrder/ViewOrder.css";
import { ReactComponent as DownloadAllIcon } from "../../assets/image/svg/downloadAllWhite.svg";
import "../manageBasket/ManageBasket.css";
import { Loader } from "../../components/loader/Loader";
import {
  filterData,
  searchWithStatusFilter,
  updateDatesInLocalStorage,
} from "../../components/dms_common/DmsCommonServices";
import { isGapMoreThanNDays, isStartDateIsGreaterThanEndDate } from "../../utils/calenderUtils";
import { downloadAllReports } from "../../services/manageReportService";
import Header from "../../components/dms_common/Header";
import useSessionStorageDates from "../../components/dms_common/UseSessionStorageDates";
import { getConfigData } from "../../services/configurationService";
import { analytics, logLoadTrace } from "../../../src/firebase";
import { logEvent } from "firebase/analytics";
import { fetchSessionStorageFilter, safeStringify, updateSessionStorageFilter } from "../../components/dms_common/DmsUtils";
import { FilterKeys } from "../../types/types";
import { getDownloadSingleReportConfig } from "../secondaryOrder/ManageSecondaryOrderUtils";
import { sendSlackAlert, SlackChannel } from "../../services/slackService";

const ViewAllGrn = () => {
  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );
  const startDateAtLastNDays = ConfigDictionary?.startDateAtLastNDays ?? 0
  const newStartDate = dayjs().subtract(startDateAtLastNDays, 'day')
  const { storedStartDate, storedEndDate, storeSessionStorageDates } = useSessionStorageDates("viewAllGrns", newStartDate)
  const filterValue = fetchSessionStorageFilter(FilterKeys.PRIMARY_ViewAllGrn ) ?? "OPEN";
  updateSessionStorageFilter(FilterKeys.PRIMARY_ViewPrimaryOrders, "ALLORDERS")
  const feature = "Purchase";
  const pageID = "ViewAllGRN";

  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [data, setData] = useState<grnRowsData[]>([]);
  const [rows, setRows] = useState<grnRowsData[]>([]);
  const [grnStages, setGrnStages] = useState<any[]>([]);
  const [loader, setLoader] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs>(storedStartDate);
  const [endDate, setEndDate] = useState<Dayjs>(storedEndDate);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>(filterValue);
  const [resetSearch, setResetSearch] = useState(false);
  const primaryOrderTrackingStagesRef = useRef<any[]>([]);
  const allGrnsReportIdRef = useRef<string | null>(null);
  const singleGrnReportIdRef = useRef<string | null>(null);
  const [downloadSingleReportConfig, setDownloadSingleReportConfig] =
      useState<DownloadSingleReportConfig | null>();
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const openPopup = useCallback((message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  }, []);

  const validDateRange = ConfigDictionary.dateRangeConfig ?? 31;
  const validateDateRange = () => {
    if (startDate && endDate && isGapMoreThanNDays(startDate, endDate, validDateRange)) {
      openPopup(`You can't select date range more than ${validDateRange} days!`, "Alert");
      return false;
    }
    if (startDate && endDate && isStartDateIsGreaterThanEndDate(startDate, endDate)) {
      openPopup("To Date should be greater than from date.", "Alert");
      return false;
    }
    updateDatesInLocalStorage(startDate, endDate, feature, pageID);
    return true;
  };

  const handleDatesChange = async () => {
    if (isButtonLoading || !validateDateRange()) return; // Prevent multiple clicks
    setIsButtonLoading(true);
    try {
      storeSessionStorageDates(startDate, endDate);
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");
      await getAllGrnData(start, end);
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleResetDates = async () => {
    if (isButtonLoading) return; // Prevent multiple clicks
    setIsButtonLoading(true);
    try {
      const date = dayjs();
      storeSessionStorageDates(newStartDate, date);
      const start = getUTCDate(newStartDate, "start");
      const end = getUTCDate(date, "end");
      setStartDate(newStartDate);
      setEndDate(date);
      await getAllGrnData(start, end);
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleDownloadAll = async() => {
    if (isButtonLoading) return; // Prevent multiple clicks
    setIsButtonLoading(true);
    try {
      const startTime = window.performance.now();
      await downloadAllReports(allGrnsReportIdRef.current, setSnackbarOpen, startDate, endDate, "type", "primary");

      const endTime = window.performance.now();
      logEvent(analytics, "grns_report_downloaded", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "download_format": "xlsx",
        "download_time": (endTime - startTime),
      });

    } catch(error){
      await sendSlackAlert(
        SlackChannel.DMS_PRIMARY_UI_NOTIFICATION,
        "Download All GRNs Failed - ViewAllGrn.tsx",
        `Failed to download all GRN reports for date range ${startDate.format("YYYY-MM-DD")} to ${endDate.format("YYYY-MM-DD")}. Error: ${(error as Error).message}`,
        safeStringify({ error, startDate, endDate, allGrnsReportId: allGrnsReportIdRef.current })
      );
      logEvent(analytics, "grns_report_download_failed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "error_reason": error
      });
    }
    finally {
      setIsButtonLoading(false);
    }
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const handleStatusFilterChange = (newFilter: string) => {
    setStatusFilter(newFilter);
    updateSessionStorageFilter(FilterKeys.PRIMARY_ViewAllGrn, newFilter);
  };

  const getAllGrnData = async (startDate: string, endDate: string) => {
   setResetSearch(true); 
    setTimeout(() => setResetSearch(false), 0); 

    setLoader(true);
    try {
      const response = await getGrnList(startDate, endDate);
      let totalGrnData: grnRowsData[] = [];
      let filteredGrnData: grnRowsData[] = [];
      response?.data?.features?.forEach((item: any) => {
        const {
          orderNumber,
          invoiceNumber,
          creationTime,
          grnStatus,
          orderStatus: status,
          netAmount,
          salesDetails,
          totalQuantity,
          totalCaseQuantity,
          totalPieceQuantity,
          totalOtherUnitQuantity
        } = item;

        const totalInitialCaseQuantity = totalCaseQuantity;
        const totalInitialPieceQuantity = totalPieceQuantity
        const totalInitialOtherQuantity = totalOtherUnitQuantity;

        const grnObj = {
          id:invoiceNumber,
          orderNumber,
          invoiceNumber,
          creationTime,
          totalCaseQuantity,
          totalPieceQuantity,
          netAmount,
          status,
          grnStatus,
          totalQuantity,
          showEyeIcon : true,
          redirectViewGRNicon:true,
          totalInitialCaseQuantity ,
          totalInitialPieceQuantity ,
          totalInitialOtherQuantity 
        };
        totalGrnData.push(grnObj);
      });
      
      filteredGrnData = totalGrnData.sort((a, b) => {
        const dateA = new Date(a.creationTime);
        const dateB = new Date(b.creationTime);
        return dateB.getTime() - dateA.getTime(); 
      });
      totalGrnData = totalGrnData.sort((a, b) => {
        const dateA = new Date(a.creationTime);
        const dateB = new Date(b.creationTime);
        return dateB.getTime() - dateA.getTime(); 
      });
      
      setRows(filteredGrnData);
      setData(totalGrnData);
    } catch (error) {
      await sendSlackAlert(
        SlackChannel.DMS_PRIMARY_UI_NOTIFICATION,
        "Error Fetching GRN Data - ViewAllGrn.tsx",
        `Failed to fetch GRN data for date range ${startDate} to ${endDate}. Error: ${(error as Error).message}`,
        safeStringify({ error, startDate, endDate, statusFilter })
      );
      openPopup("Something went wrong while fetching GRNs", "Error");
    } finally {
      setLoader(false);
    }
  };


  const getInitalTableData = async () => {
    setStartDate(startDate);
    setEndDate(endDate);
    const start = getUTCDate(startDate, "start");
    const end = getUTCDate(endDate, "end");
    await getAllGrnData(start, end);
  };

  const fetchRequiredConfigs = async () => {

    const stages = await getConfigData('order_configuration','grnStages');
    const primaryOrderStages = await getConfigData('order_configuration',"primaryOrderTrackingStages");
    
    const primaryOrderTrackingStages = primaryOrderStages[0]?.configValue ?? []
    const grnStages = stages[0]?.configValue ?? []
    const allGrnsReportId = await getConfigData('order_configuration',"allGrnsReportId");
    const singleGrnReportId = await getConfigData('order_configuration',"singleGrnReportId");


    const grnReportIdConfig = await getConfigData(
      "dms_configuration",
      "grnReportNameId"
    )
    const grnReportId = grnReportIdConfig[0]?.configValue ?? "PrimaryGRNReport";

    setDownloadSingleReportConfig(getDownloadSingleReportConfig(grnReportId));
    allGrnsReportIdRef.current = allGrnsReportId[0]?.configValue ?? grnReportId;
    singleGrnReportIdRef.current = singleGrnReportId[0]?.configValue ?? null;
    primaryOrderTrackingStagesRef.current = primaryOrderTrackingStages;
    setGrnStages([{
      id: "ALLGRN",
      color: "#F0F3FF",
      dmsLabel: "All GRN",
      labelColor: "",
    },...grnStages]);
  };

  useEffect(() => {
    logLoadTrace(init,"view_all_grn_page_load_time");
  }, []);

  const init = async () => {
    if (isButtonLoading) return; // Prevent if already processing
    setIsButtonLoading(true);
    try {
    setLoader(true);
    await Promise.allSettled([fetchRequiredConfigs(),getInitalTableData()])
    }
    finally{
      setLoader(false);
      setIsButtonLoading(false);
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      getInitalTableData();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [startDate, endDate]);
  const filteredData = useMemo(() => {
    const res = filterData(data, searchedValue.toLowerCase(), searchWithStatusFilter, statusFilter);
    return res
  }, [searchedValue, statusFilter, data]);


  useEffect(() => {
    if(!loader) {
      logEvent(analytics, "grn_list_viewed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "total_grns_displayed": filteredData.length
      });
      console.log("grn-list",filteredData.length)
    }
  },[filteredData,loader,statusFilter]);

  useEffect(() => {
      logEvent(analytics, "primary_module_accessed", {
        "entry_time" : new Date().toISOString(),
      });
      console.log("enter-primary",new Date().toISOString())
  },[])

  useEffect(() => {
    if(searchedValue){
      logEvent(analytics, "search_grn", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "search_query" : searchedValue,
        "result_count": filteredData.length
      });
      console.log("search-grn",filteredData.length,searchedValue)
      }
  },[searchedValue]);

  useEffect(() => {
    setRows(filteredData);
  }, [filteredData]);


  return (
    <div className="primary-order-parent">
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>

      <Header
        title={"View All GRNs"} 
        searchFunction={loader ? () => { } : handleSearchValueChange} 
        onRefresh={init}
        resetSearch={resetSearch}
      />

      <div className="primary-order-filter-bar">
        <div className="primary-order-date-action-btn-container">
          <Stack spacing={2} direction={"row"}>
            <LocalizationProvider dateAdapter={AdapterDayjs} className="dateEnter">
  <DatePicker
    label="From Date"
    inputFormat="DD-MM-YYYY"
    value={startDate}
    maxDate={dayjs()}
    onChange={(value) => {
      if (value) {
        setStartDate(value);
      }
    }}
    
    renderInput={(props) => <TextField {...props} inputProps={{
      ...props.inputProps,
      readOnly: true,
    }} disabled size="small" />}
  />
</LocalizationProvider>

<LocalizationProvider dateAdapter={AdapterDayjs} className="dateEnter">
  <DatePicker
    label="To Date"
    inputFormat="DD-MM-YYYY"
    value={endDate}
    maxDate={dayjs()}
    onChange={(value) => {
      if (value) {
        setEndDate(value);
      }
    }}
    
    renderInput={(props) => <TextField {...props} inputProps={{
      ...props.inputProps,
      readOnly: true,
    }} disabled size="small" />}
  />
</LocalizationProvider>

          </Stack>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#237C9D", borderRadius:"6px",textTransform: "none",boxShadow: "none",  }}
            onClick={handleDatesChange}
          >
            Apply
          </Button>
          <Button variant="outlined"  sx={{ backgroundColor: "#F0FBFF", borderRadius:"6px", textTransform: "none",boxShadow: "none"}} onClick={handleResetDates}>
            Reset
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              minWidth: "170px",
              backgroundColor: "var(--dashboard-theme-color)",
              ":hover": {
                background: "var(--dashboard-theme-color)",
              },
               borderRadius:"6px",
               boxShadow: "none",
            }}
            onClick={handleDownloadAll}
          >
             <DownloadAllIcon style={{ marginRight: "6px" }} />
            Download All
          </Button>
        </div>
        <div className="primary-order-filter-container">
          <InputLabel id="primary-filter-dropdown" sx={{color:"#B2B2B2",fontSize:"15px"}}>Filter:</InputLabel>
          <FormControl size="small" sx={{ width: "70%" }}>
            <Select
              labelId="primary-filter-dropdown"
              value={statusFilter}
              onChange={(e) => {
                handleStatusFilterChange(e.target.value);
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#DEDEDE'
                },
              '& .MuiSelect-select': {
        color: '#5C5C5C',      
        fontSize: '14px',      
        fontWeight: 500,       
      },
              }}
            >
              {grnStages.map((option) => (
                <MenuItem key={option.id} value={option.id} sx={{
                  color: '#5C5C5C',      
                  fontSize: '14px',      
                  fontWeight: 500,       
                }}>
                  {option.dmsLabel}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      {loader ? (
        <Loader />
      ) : (
        <CustomDataGrid
          rows={rows}
          columnsMetaData={ViewAllGrnConfig}
          downloadSingleReportConfig={downloadSingleReportConfig}
          orderStatusConfig={primaryOrderTrackingStagesRef.current}
          grnStatusConfig={grnStages}
          redirectUrl="/purchase/grn/viewGrnDetails/"
          openButtonNavigateUrl="purchase/grn"
          tableWidth={0.73}
          enableRowClickRedirect={true}
          isPrimaryModuleReport={true}
        />
      )}
    </div>
  );
};

export default ViewAllGrn;
